import classNames from 'classnames';
import React, { useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ROUTES } from 'shared/constants';
import styles from './Breadcrumbs.module.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight } from '@fortawesome/pro-solid-svg-icons';
import {
  getSelectedWorkspace,
  getSelectedWorkspaceId,
} from 'shared/store/selectors/workspace.selector';
import { useSelector } from 'react-redux';
import { Button } from '../design-system';

const BreadcrumbsComponent = React.memo(
  ({
    path = [],
    title = '',
    categoryPath = ROUTES.CONTENT_ITEM,
    rootPath = ROUTES.KNOWLEDGE,
    rootTitle = null,
    wrapperStyle = null,
    hideRoot = false,
    onCurrentClick,
    currentTo,
    currentItemId,
    currentQueryParamKey,
    sectionLinks = false,
    workspaceName = '',
    onPathClick,
    initialWorkspaceId,
  }) => {
    const workspace = useSelector(getSelectedWorkspace);
    const currentWorkspaceId = useSelector(getSelectedWorkspaceId);
    const rootName = rootTitle ?? workspace?.name;

    const needsWorkspaceSwitch = !!initialWorkspaceId && initialWorkspaceId !== currentWorkspaceId;

    const buildUrl = useCallback(
      (basePath, { hash, queryParams } = {}) => {
        const params = new URLSearchParams();
        if (queryParams) {
          Object.entries(queryParams).forEach(([k, v]) => params.set(k, v));
        }
        if (needsWorkspaceSwitch) {
          params.set('workspace', initialWorkspaceId);
        }
        const query = params.toString();
        const hashPart = hash ? `#${hash}` : '';
        return `${basePath}${query ? `?${query}` : ''}${hashPart}`;
      },
      [needsWorkspaceSwitch, initialWorkspaceId],
    );

    const computedCurrentTo = useMemo(() => {
      if (currentTo) return currentTo;
      if (!currentItemId || !currentQueryParamKey) return null;
      const lastDirIndex = path.length % 2 === 0 ? path.length - 1 : path.length - 2;
      const parentId = lastDirIndex >= 0 ? path[lastDirIndex]?.id : null;
      const basePath = parentId ? categoryPath.replace(':id', parentId) : rootPath;
      return buildUrl(basePath, {
        queryParams: { [currentQueryParamKey]: currentItemId },
      });
    }, [currentTo, currentItemId, currentQueryParamKey, path, categoryPath, rootPath, buildUrl]);

    return (
      <div className={classNames(styles.root, wrapperStyle)}>
        {!hideRoot && (
          <>
            <Link to={buildUrl(rootPath)} onClick={onPathClick}>
              {rootName}
            </Link>
            <FontAwesomeIcon icon={faChevronRight} className={styles.arrow} />
          </>
        )}
        {workspaceName && (
          <div className={styles.path}>
            <Link to={buildUrl(rootPath)} onClick={onPathClick}>
              {workspaceName}
            </Link>
            <FontAwesomeIcon icon={faChevronRight} className={styles.arrow} />
          </div>
        )}
        {path.map(({ id, title }, i) => {
          const isDir = i % 2 !== 0;
          let to = null;

          if (isDir) {
            to = buildUrl(categoryPath.replace(':id', id));
          } else if (sectionLinks) {
            const parentDirPath = i === 0 ? rootPath : categoryPath.replace(':id', path[i - 1].id);
            to = buildUrl(parentDirPath, { hash: id });
          }

          return (
            <div key={`breadcrumbs-${id}`} className={styles.path}>
              {to ? (
                <Link onClick={e => onPathClick?.(e, id)} to={to}>
                  {title}
                </Link>
              ) : (
                title
              )}
              <FontAwesomeIcon icon={faChevronRight} className={styles.arrow} />
            </div>
          );
        })}
        {computedCurrentTo ? (
          <Link className={styles.lastItem} to={computedCurrentTo} onClick={onPathClick}>
            {title}
          </Link>
        ) : onCurrentClick ? (
          <Button className={styles.lastItem} onClick={() => onCurrentClick()} type="link">
            <span>{title}</span>
          </Button>
        ) : (
          <span>{title}</span>
        )}
      </div>
    );
  },
);

export { BreadcrumbsComponent };
